/*
 * Copyright © 2021-present Tacnode Inc. All rights reserved.
 * The information contained in this software is confidential and proprietary to Tacnode Inc. and its subsidiaries. No part of this software may be used, copied, reproduced, modified, displayed, performed, executed, published, distributed, conveyed, stored, transmitted, or otherwise exploited in any form or by any means, including but not limited to electronic, mechanical, photocopying, recording, transcribing, without the prior written consent of Tacnode Inc.
 * Any violation of the above terms will constitute an infringement of copyright and will be subjected to legal action.
 */
import { ArrowUpOutlined } from '@ant-design/icons'
import { Button, DescriptionsProps, Space } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import classNames from 'classnames'
import styles from './index.less'
import { Replicable } from '@/components/Replicable'
import { ALL_PAGES, IGNORE_ERR_HEADER } from '@/constants'
import { UpgradeContractVersion } from '@/pages/contract/features/UpgradeContractVersion'
import { ContractWithVersion } from '@/pages/contract/interface'
import { CreateContextlakeModal } from '@/pages/contextlake/Contextlakes/CreateContextlakeModal'
import { contractIcon } from '@/process.env'
import { accountClient } from '@/services/cloudClient/accountClient'
import { timeFormatter } from '@/utils/timeFormatter'

export function ContractCard(
  props: React.PropsWithoutRef<{
    contract: ContractWithVersion
  }>,
) {
  const { contract } = props
  const { id, signID, tags, creator, commencingOn, buyerName } = contract
  const { t } = useTranslation()
  const [contextlakes, setContextlakes] = useState<number>(1)

  const items: DescriptionsProps['items'] = [
    {
      key: 'Contract ID',
      label: t('Contract ID'),
      children: <Replicable value={signID}>{signID}</Replicable>,
    },
    {
      key: 'Personal Name',
      label: t('Company Name'),
      children: buyerName,
    },
    {
      key: 'Creator',
      label: t('Creator'),
      children: creator,
    },
    {
      key: 'Created at',
      label: t('Created at'),
      children: timeFormatter.format(commencingOn),
    },
    {
      key: 'Context lakes Count',
      label: t('Context lakes Count'),
      children: contextlakes > 0 ? <Link to={`/contracts/${id}/contextlakes`}>{contextlakes}</Link> : 0,
    },
  ]

  useEffect(() => {
    accountClient.contextlakesApi
      .listContextlakes({ contractId: contract.id, ...ALL_PAGES }, IGNORE_ERR_HEADER)
      .then(res => setContextlakes(res.data.items.length))
  }, [contract])

  return (
    <div className={classNames('common-card', styles.container)}>
      <div className={styles.header}>
        <Link to={`/contracts/${id}/overview`}>{tags}</Link>
        <Space>
          <CreateContextlakeModal contractId={id} size="small" />
          <UpgradeContractVersion
            contracts={[contract]}
            trigger={
              <Button size="small" icon={<ArrowUpOutlined />}>
                {t('Upgrade')}
              </Button>
            }
          />
        </Space>
      </div>
      <div className={styles.main}>
        <img className={styles.mask} src={contractIcon} />
        <>
          {items.map(
            item =>
              item && (
                <div key={item.key} className={styles.item}>
                  <span className={styles.label}>{item.label}</span>
                  <span>{item.children}</span>
                </div>
              ),
          )}
        </>
      </div>
    </div>
  )
}
